import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Payment as PaymentIcon,
  Gavel as GavelIcon,
  VerifiedUser as VerifiedIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  BuildCircle as BuildCircleIcon,
  Notifications as NotificationsIcon,
  BugReport as BugReportIcon,
  EmojiEvents as BadgesIcon,
  Assessment as ReportsIcon,
  History as AuditIcon,
  AdminPanelSettings as AdminUsersIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import adminAlertsService from '../services/api/adminAlertsService';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'CORE',
    items: [
      { label: 'Dashboard', icon: DashboardIcon, path: '/dashboard' },
      { label: 'Users', icon: PeopleIcon, path: '/users' },
      { label: 'Jobs', icon: WorkIcon, path: '/jobs' },
      { label: 'Services', icon: BuildCircleIcon, path: '/services' },
      { label: 'Verifications', icon: VerifiedIcon, path: '/verifications' },
    ],
  },
  {
    title: 'FINANCE',
    items: [
      { label: 'Payments', icon: PaymentIcon, path: '/payments' },
      { label: 'Disputes', icon: GavelIcon, path: '/disputes' },
    ],
  },
  {
    title: 'ENGAGEMENT',
    items: [
      { label: 'Admin Alerts', icon: NotificationsIcon, path: '/admin-alerts' },
      { label: 'Notifications', icon: NotificationsIcon, path: '/notifications' },
      { label: 'Bug Reports', icon: BugReportIcon, path: '/admin/support/bugreport' },
      { label: 'Badges', icon: BadgesIcon, path: '/badges' },
    ],
  },
  {
    title: 'INSIGHTS',
    items: [
      { label: 'Reports', icon: ReportsIcon, path: '/reports' },
      { label: 'Audit Logs', icon: AuditIcon, path: '/audit-logs' },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { label: 'Admin Users', icon: AdminUsersIcon, path: '/admin-users' },
      { label: 'Settings', icon: SettingsIcon, path: '/settings' },
    ],
  },
];

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [alertCount, setAlertCount] = useState(0);

  const fetchAlertCount = useCallback(async () => {
    try {
      const counts = await adminAlertsService.getCounts();
      setAlertCount(counts.total);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchAlertCount();
    const id = setInterval(fetchAlertCount, 120_000); // refresh every 2 min
    return () => clearInterval(id);
  }, [fetchAlertCount]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileSettings = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Logo Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/images/logo.png"
          alt="Daily2K Logo"
          sx={{ height: 36, width: 'auto', objectFit: 'contain' }}
          onError={(e: any) => { e.target.style.display = 'none'; }}
        />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1rem' }}>
            Daily2K
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
            Admin Panel
          </Typography>
        </Box>
      </Box>

      {/* Admin info strip */}
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>
          Logged in as
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem', lineHeight: 1.3 }}>
          {user?.first_name} {user?.last_name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
          {user?.email}
        </Typography>
      </Box>

      {/* Navigation Sections */}
      <Box sx={{ overflowY: 'auto', flexGrow: 1, py: 1 }}>
        {navSections.map((section, sIdx) => (
          <Box key={section.title}>
            {sIdx > 0 && <Divider sx={{ mx: 2, my: 0.5, borderColor: '#f1f5f9' }} />}
            <Typography
              variant="caption"
              sx={{
                px: 2.5,
                pt: sIdx === 0 ? 1 : 1.5,
                pb: 0.5,
                display: 'block',
                color: 'text.disabled',
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
              }}
            >
              {section.title}
            </Typography>
            <List dense disablePadding>
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                    <ListItemButton
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        borderRadius: '0.5rem',
                        mb: 0.25,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: active ? 'primary.light' : 'transparent',
                        color: active ? 'primary.dark' : 'text.secondary',
                        '&:hover': {
                          bgcolor: active ? 'primary.light' : '#f8fafc',
                          color: active ? 'primary.dark' : 'text.primary',
                        },
                        transition: 'all 0.15s',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: active ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {item.badge ? (
                          <Badge badgeContent={item.badge} color="error">
                            <item.icon fontSize="small" />
                          </Badge>
                        ) : (
                          <item.icon fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: active ? 700 : 500,
                        }}
                      />
                      {active && (
                        <ChevronRightIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Bottom Logout */}
      <Box sx={{ p: 1, borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: '0.5rem',
            px: 1.5,
            py: 0.75,
            color: 'error.main',
            '&:hover': { bgcolor: 'error.light' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Current page breadcrumb */}
          <Typography variant="body2" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
            {navSections
              .flatMap((s) => s.items)
              .find((item) => isActive(item.path))?.label ?? 'Admin'}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {/* Alert Bell */}
          <Tooltip title="Admin alerts">
            <IconButton
              onClick={() => navigate('/admin-alerts')}
              size="small"
              sx={{ mr: 1 }}
            >
              <Badge badgeContent={alertCount || undefined} color="error" max={99}>
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile */}
          <Tooltip title="Account settings">
            <IconButton onClick={handleProfileMenuOpen} size="small">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                }}
              >
                {user?.first_name?.[0]?.toUpperCase() ?? 'A'}
              </Box>
            </IconButton>
          </Tooltip>
          <Typography sx={{ ml: 1, fontWeight: 600, display: { xs: 'none', sm: 'block' }, fontSize: '0.85rem' }}>
            {user?.first_name} {user?.last_name}
          </Typography>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{ elevation: 2, sx: { mt: 1, minWidth: 180 } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={600}>{user?.first_name} {user?.last_name}</Typography>
              <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfileSettings} sx={{ fontSize: '0.875rem' }}>
              <AccountIcon sx={{ mr: 1.5, fontSize: 18 }} /> Profile Settings
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main', fontSize: '0.875rem' }}>
              <LogoutIcon sx={{ mr: 1.5, fontSize: 18 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer - Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            borderRight: '1px solid #f1f5f9',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Drawer - Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '56px',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
